import { Icon } from '@iconify/react';
import { LoadingButton } from '@mui/lab';
import { Box, Stack, Typography, Tooltip } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PropTypes from 'prop-types';
import { useEffect, useState, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useParams } from 'react-router-dom';
import { generateInvoicePdfFromData } from '../../../../api/invoicing';
import { addInvoiceFile, getInvoiceFiles, renderInvoiceFile, addInvoiceLog, getInvoiceOCRInProgress } from '../../../../redux/slices/invoicing';
import { useDispatch, useSelector } from '../../../../redux/store';
import { useIsInvoiceEditable } from './reusable/hooks/useInvoiceLocks';
import PDFViewer from '../../../../reusable-components/pdf-viewer/PDFViewer';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { InvoiceOCRStatus } from '../../../../utils/globalEnums';

InvoicePdfAttachment.propTypes = {
  selectedFileId: PropTypes.number,
  highlightRegion: PropTypes.object,
};
export default function InvoicePdfAttachment({ selectedFileId, highlightRegion }) {
  const { id } = useParams();
  const dispatch = useDispatch();
  const {
    data: { invoicePhysicalFile, invoiceFiles, invoiceItemData },
  } = useSelector((state) => state.invoicing);
  const { enqueueSnackbar } = useSnackbar();
  const { isEditable } = useIsInvoiceEditable(true);
  const {
    getRootProps,
    getInputProps,
    open: openDropzone,
  } = useDropzone({
    accept: {
      'application/pdf': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [], // .docx
      'application/msword': [], // .doc
      'application/vnd.ms-excel': [], // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [], // .xlsx
    },
    onDrop: (acceptedFiles) => {
      if (acceptedFiles[0] === undefined) return;
      addFile(acceptedFiles[0]);
    },
  });
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMainFile, setIsMainFile] = useState(invoicePhysicalFile);
  const [ocrStatus, setOcrStatus] = useState(InvoiceOCRStatus[invoiceItemData?.invoice?.invoiceOCRStatus] || InvoiceOCRStatus.None);
  const [fileUploaded, setFileUploaded] = useState(false);
  const timeoutRef = useRef(null);


  useEffect(() => {
    //when file is clicked from InvoiceAttachments
    setIsMainFile(selectedFileId === 0);
    if (selectedFileId === 0) return;
    const mainFileId = invoiceFiles?.find((f) => f.invoiceAttachmentCategoryId === 1)?.id;
    const mainFile = mainFileId === id;
    setIsMainFile(mainFile);
    dispatch(renderInvoiceFile(mainFile ? id : selectedFileId, mainFile));
  }, [selectedFileId]);

  async function fetchInvoiceFile() {
    await dispatch(renderInvoiceFile(id, true));
    setDataLoaded(true);
  }
  useEffect(() => {
    //first time loading invoice view
    fetchInvoiceFile();
  }, [id]);
  const generatePDF = async (e) => {
    e.stopPropagation();
    setIsSubmitting(true);
    try {
      const response = await generateInvoicePdfFromData(id);
      if (response.status === 200) {
        dispatch(renderInvoiceFile(id));
        enqueueSnackbar('PDF generated successfully', { variant: 'success' });
        await dispatch(getInvoiceFiles(id));
      }
    } catch (error) {
      enqueueSnackbar('PDF generation failed', { variant: 'error' });
    }

    setIsSubmitting(false);
  };

  const addFile = async (file) => {
    const response = await dispatch(addInvoiceFile(id, file));
    if (response === 'success') {
      dispatch(renderInvoiceFile(id));
      enqueueSnackbar('File added successfully', { variant: 'success' });
      setFileUploaded(true);
    } else {
      enqueueSnackbar('Failed to upload file', { variant: 'error' });
    }
  };

  useEffect(() => {
    let pollCount = 0;
    const MAX_POLLS = 20;

    const poll = async () => {
      try {
        const status = await getInvoiceOCRInProgress(id);
        const currentStatus = status.data || InvoiceOCRStatus.None;
        setOcrStatus(currentStatus);

        if (currentStatus === InvoiceOCRStatus.InProgress) {
          pollCount++;
          const delay = pollCount <= 5 ? 1000 : 4000;

          if (pollCount < MAX_POLLS) {
            timeoutRef.current = setTimeout(poll, delay);
          }
        } else if (currentStatus === InvoiceOCRStatus.Completed) {
          dispatch(renderInvoiceFile(id));
        }
      } catch (e) {
        pollCount++;
        if (pollCount < MAX_POLLS) {
          timeoutRef.current = setTimeout(poll, 4000);
        }
      }
    };

    if ((ocrStatus === InvoiceOCRStatus.InProgress || fileUploaded) && pollCount < MAX_POLLS) {
      poll();
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setFileUploaded(false);
    };
  }, [dispatch, id, fileUploaded]);
  

  return (
    <>
      <>
        <Box>
          <div>
            {invoicePhysicalFile === null ? (
              <>
                {isMainFile ? (
                  <Box
                    sx={{
                      width: '100%',
                      border: 'dashed 1px #4ca5e7',
                      height: '430px',
                      cursor: 'pointer',
                      mt: '10px',
                    }}
                    {...getRootProps()}
                    onClick={() => {
                      if (isEditable) openDropzone();
                    }}
                  >
                    <input {...getInputProps()} />
                    <Stack
                      spacing={1}
                      direction={'column'}
                      justifyContent={'center'}
                      alignItems={'center'}
                      height={'100%'}
                    >
                      <Icon icon="arcticons:folder-upload" width="5rem" height="5rem" color="#8dc63f" />
                      <Typography color="text.secondary">Drop invoice or upload file</Typography>
                      {invoiceItemData?.invoice?.sourceMethod !== 'Manual' && (
                        <LoadingButton
                          color="secondary"
                          size="large"
                          type="submit"
                          variant="outlined"
                          loading={isSubmitting}
                          onClick={generatePDF}
                          disabled={!isEditable}
                          sx={{
                            p: 1.5,
                            ml: 4,
                          }}
                        >
                          Generate PDF invoice
                        </LoadingButton>
                      )}
                    </Stack>
                  </Box>
                ) : (
                  <Box sx={{ width: '100%', maxHeight: '600px' }}>
                    <Box
                      sx={{
                        my: 1,
                        width: '25%',
                        mx: 'auto',
                        display: 'flex',
                      }}
                    >
                      <Box>Failed to load PDF file.</Box>
                      {invoiceItemData?.invoice?.sourceMethod === null && (
                        <LoadingButton
                          color="secondary"
                          type="submit"
                          loading={isSubmitting}
                          onClick={generatePDF}
                          disabled={!isEditable}
                          sx={{ p: 0.5 }}
                        >
                          <Tooltip title="Generate PDF invoice">
                            <PictureAsPdfIcon />
                          </Tooltip>
                        </LoadingButton>
                      )}
                    </Box>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ mt: '10px' }}>
                {dataLoaded && (
                  <PDFViewer
                    blobUrl={invoicePhysicalFile}
                    highlightRegion={highlightRegion}
                    onDownload={() => {
                      dispatch(addInvoiceLog({ invoiceId: id, messageType: 'Download' }));
                    }}
                    fileName={`Invoice_PS #${invoiceItemData?.invoice?.vendorInvoiceId}`}
                  />
                )}
              </Box>
            )}
          </div>
        </Box>
      </>
    </>
  );
}
